import { Button, Divider, Dropdown, Space, Tooltip } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import * as React from 'react';
import { useMemo, useEffect, useCallback } from 'react';
import styles, { DynamicSvg, entries, ShapeType } from '..';
import { useGlobalShapeSelector } from '@/contexts/GlobalShapesContext';
import { ToolbarButtonCategory as ShapeGroup } from '../types/commontypes';
import { useLocalStorage } from '../hooks/use-local-storage';

interface ToolbarProps extends Omit<React.HTMLAttributes<HTMLElement>, 'onClick'> {
  isShapeListViewOpen?: boolean;
  shapeTypeCaptions?: Record<ShapeType, string>;
  onShapeListToggle: () => void;
}

// Category to shape types mapping
const CATEGORY_SHAPE_MAP: Record<ShapeGroup, ShapeType[]> = {
  [ShapeGroup.MEASURE]: [ShapeType.MEASURE],
  [ShapeGroup.LINES]: [
    ShapeType.TREND_LINE,
    ShapeType.RAY,
    ShapeType.INFO_LINE,
    ShapeType.HORZ_LINE,
    ShapeType.VERT_LINE,
    ShapeType.PARALLEL_CHANNEL,
  ],
  [ShapeGroup.VOLUME]: [ShapeType.FIXED_RANGE_VOLUME_PROFILE, ShapeType.ANCHORED_VOLUME_PROFILE],
  [ShapeGroup.FIBONACCI]: [ShapeType.FIB_RETRACEMENT, ShapeType.FIB_CHANNEL],
  [ShapeGroup.GANN]: [ShapeType.GANN_FAN, ShapeType.GANN_BOX],
  [ShapeGroup.PATTERNS]: [ShapeType.ELLIOTT_IMPULSE_12345, ShapeType.ELLIOTT_CORRECTION_ABC, ShapeType.XABCD_PATTERN],
  [ShapeGroup.GEOMETRIC_SHAPES]: [ShapeType.RECTANGLE, ShapeType.TRIANGLE, ShapeType.PATH],
  [ShapeGroup.TEXT]: [ShapeType.TEXT],
  [ShapeGroup.SHAPE_LIST_VIEW]: [],
};

export const Toolbar: React.FC<ToolbarProps> = ({
  isShapeListViewOpen,
  shapeTypeCaptions,
  onShapeListToggle,
  className,
  ...restProps
}) => {
  // Get from GlobalShapesContext
  const shapeTypeToDraw = useGlobalShapeSelector(s => s.shapeTypeToDraw);
  const setShapeTypeToDraw = useGlobalShapeSelector(s => s.setShapeTypeToDraw);

  // Track last selected shape per category
  const [lastSelectedShapeTypeByCategory, setLastSelectedShapeTypeByCategory] = useLocalStorage<
    Partial<Record<ShapeGroup, ShapeType>>
  >('lastSelectedShapeTypeByCategory', {});

  // Update last selected shape when a shape is selected
  useEffect(() => {
    if (shapeTypeToDraw) {
      // Find which category this shape belongs to
      for (const [category, shapes] of entries(CATEGORY_SHAPE_MAP)) {
        if (shapes.includes(shapeTypeToDraw)) {
          setLastSelectedShapeTypeByCategory(prev => ({
            ...prev,
            [category]: shapeTypeToDraw,
          }));
          break;
        }
      }
    }
  }, [shapeTypeToDraw, setLastSelectedShapeTypeByCategory]);

  const handleShapeClick = useCallback(
    (type: ShapeType) => {
      if (shapeTypeToDraw === type) {
        // Force re-selection
        setShapeTypeToDraw(undefined);
        setTimeout(() => {
          setShapeTypeToDraw(type);
        }, 0);
      } else {
        setShapeTypeToDraw(type);
      }
    },
    [shapeTypeToDraw, setShapeTypeToDraw],
  );

  // Render toolbar buttons
  const renderToolbarButtons = useMemo(() => {
    const buttons: React.ReactNode[] = [];

    // Generate buttons from CATEGORY_SHAPE_MAP
    for (const [category, shapes] of entries(CATEGORY_SHAPE_MAP)) {
      // Skip SHAPE_LIST_VIEW here, handle it separately at the end
      if (category === ShapeGroup.SHAPE_LIST_VIEW) {
        continue;
      }

      const defaultShape = shapes.length > 1 ? lastSelectedShapeTypeByCategory[category] || shapes[0] : shapes[0];

      // Categories with children (submenu) - Use Space.Compact + Button + Dropdown
      if (shapes.length > 1) {
        const dropdownMenu: MenuProps = {
          items: shapes.map(type => ({
            key: type,
            label: shapeTypeCaptions?.[type] || type,
            icon: <DynamicSvg iconName={type} />,
            onClick: () => handleShapeClick(type),
          })),
        };

        buttons.push(
          <Space.Compact key={category} orientation="horizontal" style={{ display: 'flex', position: 'relative' }}>
            <Tooltip key={category} placement="right" title={shapeTypeCaptions?.[defaultShape] || defaultShape}>
              <Button
                type={shapeTypeToDraw === defaultShape ? undefined : 'text'}
                color={shapeTypeToDraw === defaultShape ? 'default' : undefined}
                variant={shapeTypeToDraw === defaultShape ? 'filled' : undefined}
                icon={<DynamicSvg iconName={defaultShape} />}
                onClick={() => handleShapeClick(defaultShape)}
              />
            </Tooltip>
            {/* @ts-ignore: force passing 'rightTop' to placement */}
            <Dropdown
              classNames={{ root: styles.shapeMenu }}
              menu={dropdownMenu}
              placement="topRight"
              trigger={['hover']}
              align={{ offset: [-4, 0] }}
            >
              <Button
                type={shapeTypeToDraw === defaultShape ? undefined : 'text'}
                color={shapeTypeToDraw === defaultShape ? 'default' : undefined}
                variant={shapeTypeToDraw === defaultShape ? 'filled' : undefined}
                size="small"
                icon={<RightOutlined style={{ width: '8px' }} />}
              />
            </Dropdown>
          </Space.Compact>,
        );
      } else if (shapes.length === 1) {
        // Categories with single shape (no submenu) - Use single Button
        buttons.push(
          <Tooltip key={category} placement="right" title={shapeTypeCaptions?.[defaultShape] || defaultShape}>
            <Button
              type={shapeTypeToDraw === defaultShape ? undefined : 'text'}
              color={shapeTypeToDraw === defaultShape ? 'default' : undefined}
              variant={shapeTypeToDraw === defaultShape ? 'filled' : undefined}
              icon={<DynamicSvg iconName={defaultShape} />}
              onClick={() => handleShapeClick(defaultShape)}
            />
          </Tooltip>,
        );
      }
    }

    // Add ShapeListView at the end if not hidden
    buttons.push(<Divider key="divider" size="small" style={{ margin: '2px' }} />);
    buttons.push(
      <Tooltip key={ShapeGroup.SHAPE_LIST_VIEW} placement="right" title="Shape List View">
        <Button
          type="text"
          className={styles.shapeListViewButton}
          data-value={isShapeListViewOpen ? 'true' : 'false'}
          icon={<DynamicSvg iconName={ShapeGroup.SHAPE_LIST_VIEW} />}
          onClick={() => onShapeListToggle()}
        />
      </Tooltip>,
    );

    return buttons;
  }, [
    shapeTypeCaptions,
    lastSelectedShapeTypeByCategory,
    handleShapeClick,
    onShapeListToggle,
    shapeTypeToDraw,
    isShapeListViewOpen,
  ]);

  return (
    <Space orientation="vertical" size="small" className={`${styles.shapeToolbar} ${className}`} {...restProps}>
      {renderToolbarButtons}
    </Space>
  );
};
